import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useRef } from 'react'
import { useSelector } from 'react-redux'
import { AccountDisplayNameFlatFields } from 'src/app/RegisterAccount/RegisterAccount'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgChevronLeft from 'src/components/Svgs/SvgChevronLeft'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { useScrollToScrollGroupRefs } from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

interface RegisterAccountDisplayNameContentProps {
  className?: string
  flatFields: AccountDisplayNameFlatFields
  onFlatFieldChange: (
    fieldName: keyof AccountDisplayNameFlatFields,
    value: AccountDisplayNameFlatFields[keyof AccountDisplayNameFlatFields],
  ) => void
  flatFieldsErrors: Partial<
    Record<
      keyof AccountDisplayNameFlatFields,
      Array<React.ReactElement | string>
    >
  >
  flatFieldsTopmostScrollGroup?: number
  onNavBack: () => void
  onSubmit: () => void
  extraPadding?: boolean
}

const RegisterAccountDisplayNameContent: React.FC<
  RegisterAccountDisplayNameContentProps
> = (props): React.ReactElement => {
  const locale = useSelector(selectNullSafeLocaleFromState)

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    props.onSubmit()
  }

  const scrollRef0 = useRef<HTMLFormElement>(null)
  useScrollToScrollGroupRefs([scrollRef0], [props.flatFieldsTopmostScrollGroup])

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        ${props.extraPadding &&
        css`
          padding: 60px;
        `}
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          ${getFontCss(
            32,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        <Trns source={"Next, let's name your account"} />
      </div>
      <div
        css={css`
          margin-top: 30px;
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        <Trns
          source={
            "Your account's display name appears at the top of the account detail screen, as well as anywhere that refers to the account. You can update it at any time."
          }
        />
      </div>
      <form
        css={css`
          display: flex;
          flex-direction: column;
          margin-top: 40px;
        `}
        onSubmit={onSubmit}
        ref={scrollRef0}
      >
        <div
          css={css`
            ${getFontCss(
              20,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
            margin-bottom: 20px;
          `}
        >
          <Trns source={'Account Display Name'} />
        </div>
        <TextInputField
          id={'accountName'}
          errors={props.flatFieldsErrors['displayName']}
          design={TextInputFieldDesigns.New}
          value={props.flatFields['displayName']}
          onChange={(val: string): void => {
            props.onFlatFieldChange('displayName', val)
          }}
          placeholder={translateSingleString(
            locale,
            'Enter Account Display Name',
          )}
        />
        <p
          css={css`
            margin-top: 8px;
            ${getFontCss(
              14,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          <Trns source={'ex: "Company Virtual Cards" or "Marketing Account"'} />
        </p>
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 40px;
            gap: 20px;
          `}
        >
          <HoverableLink
            css={css`
              display: flex;
              align-items: center;
            `}
            onClick={props.onNavBack}
            ariaLabelSource={'Back to Billing Info'}
          >
            <SvgChevronLeft />
            <Trns source={'Back to Billing Info'} />
          </HoverableLink>
          <Button
            id={'reviewButton'}
            testId="reviewButton"
            type={ButtonTypes.submit}
            design={ButtonDesigns.designCta}
            text={<Trns source={'Continue to Review and Complete'} />}
            big
          />
        </div>
      </form>
    </div>
  )
}

export default RegisterAccountDisplayNameContent
